import { isAcTerminalId } from "./ac";
import { getDefinition, type DefLookup } from "./catalog";
import { dcChargingPortGroups, dcPortTerminalGroups, isDirectDcSource, isPvTerminalId } from "./component-ports";
import { buildGraph, v } from "./diagnostics/graph";
import { circuitReach, matchesCircuitDefinition } from "./diagnostics/circuit-component-utils";
import { effectiveTerminals } from "./effective";
import type { System } from "./types";

export type BatteryProtectUse = { nodeId: string; reason: "reverse" | "bidirectional" | "model" };

/** Manufacturer use restriction, NOT an ideal diode model. OUT→IN current can
 * damage a BatteryProtect; removing that path and reporting zero would falsely
 * imply reverse protection. Keep conservative current estimates and explicitly
 * withdraw verified-route provenance for prohibited/unknown use instead.
 * Victron BatteryProtect 12/24V manual §3.1(4):
 * https://www.victronenergy.com/media/pg/BatteryProtect_12V_24V/en/installation-and-wiring-examples.html
 */
export function batteryProtectUses(system: System, lookup: DefLookup): BatteryProtectUse[] {
  const devices = system.nodes.flatMap(node => {
    if (node.kind !== "component") return [];
    const def = lookup(node.definitionId, node.customDefinition);
    return def && ["dist-victron-bp-100", "dist-victron-bp-220"].includes(def.id) &&
      system.cables.some(c => c.fromNodeId === node.id || c.toNodeId === node.id) ? [{ node, def }] : [];
  });
  if (!devices.length) return [];
  const graph = buildGraph(system, lookup, { excludeSense: true });
  const positive = new Set<string>(), batteries = new Set<string>(), generators = new Set<string>(), loads = new Set<string>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    for (const term of effectiveTerminals(def, node)) {
      if (isAcTerminalId(term.id) || isPvTerminalId(term.id) ||
          (term.polarity !== "positive" && term.polarity !== "neutral")) continue;
      positive.add(v(node.id, term.id));
      if (def.category === "battery") batteries.add(v(node.id, term.id));
    }
    if (isDirectDcSource(def) || def.category === "charger") {
      for (const term of dcChargingPortGroups(def).flat()) if (term.polarity === "positive") generators.add(v(node.id, term.id));
    }
    if (def.category === "load" || def.category === "engine" || def.category === "charger") {
      for (const term of dcPortTerminalGroups(def, "input").flat()) {
        if (term.polarity === "positive" && !isPvTerminalId(term.id)) loads.add(v(node.id, term.id));
      }
    }
  }
  const result: BatteryProtectUse[] = [];
  for (const { node, def } of devices) {
    const baseline = getDefinition(def.id)!;
    if (!matchesCircuitDefinition(def, baseline)) {
      result.push({ nodeId: node.id, reason: "model" });
      continue;
    }
    // Cut only this device's internal path. A drawn bypass remains visible;
    // it cannot establish that reverse use is safe. No source or converter is
    // turned into copper, and nonexistent terminal IDs cannot supply evidence.
    const side = (terminal: string) => {
      const reached = circuitReach(graph, v(node.id, terminal), (from, edge) =>
        positive.has(from) && positive.has(edge.vertex) &&
        !(edge.via.kind === "intra" && edge.via.link.nodeId === node.id));
      const contains = (set: Set<string>) => [...reached].some(vertex => set.has(vertex));
      return { battery: contains(batteries), generation: contains(generators), load: contains(loads) };
    };
    const input = side("in"), output = side("out");
    const reverse = (output.battery && input.load) || (output.generation && (input.battery || input.load));
    const forward = (input.battery && output.load) || (input.generation && (output.battery || output.load));
    if (input.battery && output.battery || reverse && forward) result.push({ nodeId: node.id, reason: "bidirectional" });
    else if (reverse) result.push({ nodeId: node.id, reason: "reverse" });
  }
  return result;
}
